import { CheckCircle } from 'lucide-react';
import PageWrapper from '@/components/PageWrapper';
import ImageCarousel from '@/components/ImageCarousel';
import { FadeUp, SlideLeft, SlideRight, StaggerContainer, StaggerItem } from '@/components/ScrollReveal';

const EquipmentPage = () => {
  const equipmentFeatures = [
    'Reformer for full-body strength, control, and precision',
    'Chair, Spine Corrector, Arc Barrel, and Mat for targeted support',
    'Classical Ladder Barrel for spinal articulation and extension',
    'Full Cadillac apparatus for advanced, suspended training',
  ];

  const equipmentList = [
    { name: 'Reformer', desc: 'Full-body strength and control' },
    { name: 'Chair', desc: 'Balance and targeted strength' },
    { name: 'Spine Corrector', desc: 'Spinal mobility' },
    { name: 'Arc Barrel', desc: 'Extension and flexibility' },
    { name: 'Mat', desc: 'Classical foundations' },
    { name: 'Ladder Barrel', desc: 'Spinal articulation' },
  ];

  const images = [
    { src: '/images/REFEORMER.jpg', alt: 'Reformer', caption: 'Reformer' },
    { src: '/images/REFEORMER2.jpg', alt: 'Reformer 2', caption: 'Reformer Work' },
    { src: '/images/CHAIR.jpg', alt: 'Chair', caption: 'Chair' },
    { src: '/images/LADDER BARREL HERO 1.jpg', alt: 'Ladder Barrel', caption: 'Ladder Barrel' },
    { src: '/images/CAD.jpg', alt: 'Cadillac', caption: 'Cadillac' },
  ];

  return (
    <PageWrapper>
      {/* Hero */}
      <section data-testid="equipment-hero" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeUp>
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4">Services</p>
              <h1 className="font-heading text-5xl sm:text-6xl text-stone-900 leading-[1.1] tracking-tight">
                <span className="italic">Equipment</span>
              </h1>
              <p className="mt-8 text-stone-600 text-lg leading-relaxed font-light">
                Professional Pilates apparatus for comprehensive training at every level.
              </p>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="image-zoom aspect-[4/3]">
                <img
                  src="/images/REFORMER HERO.jpg"
                  alt="Equipment"
                  className="w-full h-full object-cover editorial-image"
                />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 md:py-32 bg-stone-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <StaggerContainer className="grid md:grid-cols-2 gap-6">
            {equipmentFeatures.map((feature, index) => (
              <StaggerItem key={index}>
                <div className="flex items-start gap-4 p-6 bg-white border border-stone-200">
                  <CheckCircle className="text-stone-900 flex-shrink-0 mt-1" size={20} strokeWidth={1.5} />
                  <p className="text-stone-600 leading-relaxed font-light">{feature}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Image Carousel */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeUp>
            <ImageCarousel images={images} />
          </FadeUp>
        </div>
      </section>

      {/* Equipment Grid */}
      <section data-testid="equipment-details" className="py-24 md:py-32 bg-stone-900 text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-4">Available Equipment</p>
            <h2 className="font-heading text-4xl sm:text-5xl text-white leading-[1.1] tracking-tight mb-12">
              Full <span className="italic">Apparatus</span>
            </h2>
          </FadeUp>
          <StaggerContainer className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {equipmentList.map((item) => (
              <StaggerItem key={item.name}>
                <div className="p-6 border border-stone-700">
                  <h3 className="font-heading text-xl text-white mb-2">{item.name}</h3>
                  <p className="text-stone-400 text-sm font-light">{item.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Advanced Training */}
      <section data-testid="advanced-training" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <SlideLeft>
              <div className="image-zoom aspect-[4/3]">
                <img
                  src="/images/CAD.jpg"
                  alt="Advanced Training"
                  className="w-full h-full object-cover editorial-image"
                />
              </div>
            </SlideLeft>

            <SlideRight>
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4">Cadillac Room</p>
              <h2 className="font-heading text-4xl sm:text-5xl text-stone-900 leading-[1.1] tracking-tight">
                Advanced <span className="italic">Training</span>
              </h2>
              <p className="mt-8 text-stone-600 leading-relaxed font-light">
                Using the full Cadillac apparatus, this work explores greater strength, 
                coordination, and range of motion — including inverted and suspended exercises.
              </p>
              <span className="inline-block mt-6 px-4 py-2 text-xs uppercase tracking-wider text-stone-600 border border-stone-300">
                For Experienced Clients
              </span>
            </SlideRight>
          </div>
        </div>
      </section>
    </PageWrapper>
  );
};

export default EquipmentPage;
